import React, { lazy, Suspense } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';
import floor from 'lodash/floor';
import PropTypes from 'prop-types';

import { currencyCode } from 'Helpers/Geo/data';
import { encodeSlug } from 'Helpers/Slug';
import BrowseButton from 'Components/Utility/BrowseButton';
import FanFavouritesSection from 'Containers/HomePage/FanFavouritesSection';
import FlashDealsSection from 'Containers/AlgoliaSections/FlashDealsSection';
import GamesUnderBanner from 'Components/Sales/GamesUnderBanner';
import GenreLinks from 'Components/QuickLinks/GenreLinks';
import HitCard from 'Components/Cards/HitCard';
import MidPageBannerContainer from 'Components/Banner/MidPageBanner/MidPageBannerContainer';
import MysteryBundleCardContainer from 'Containers/Cards/MysteryBundleCardContainer';
import ProductCarouselSection from 'Containers/HomePage/ProductCarouselSection';
import PublisherCarousel from 'Components/HomePage/PublisherCarousel';
import pricing from 'Containers/StaticSearch/games-under-pricing';
import EmailSignupPlacement from 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement';

import 'Components/OnSale/OnSale.scss';

const ProductFinder = lazy(() => import(/* webpackChunkName: "ProductFinder" */ 'Components/ProductFinder/ProductFinder'));

const messages = defineMessages({
  latestDeals: {
    id: 'onsale.default.heading.latestDeals',
    defaultMessage: 'Latest Deals',
  },
  newAndUpcoming: {
    id: 'onsale.default.heading.newAndUpcoming',
    defaultMessage: 'New and Upcoming',
  },
  moreThanJustGames: {
    id: 'onsale.default.heading.moreThanJustGames',
    defaultMessage: 'More Than Just Games',
  },
  dealsUnder: {
    id: 'onsale.default.heading.dealsUnder',
    defaultMessage: 'Deals Under {price}',
  },
  dollarCollections: {
    id: 'onsale.default.heading.dollarCollections',
    defaultMessage: 'Dollar Collections',
  },
  games: {
    id: 'onsale.default.heading.games',
    defaultMessage: 'Games',
  },
  gameBundles: {
    id: 'onsale.default.heading.gameBundles',
    defaultMessage: 'Game Bundles',
  },
  questVrDeals: {
    id: 'onsale.default.heading.questVrDeals',
    defaultMessage: 'Quest VR Deals',
  },
  steamDeckCompatibleGames: {
    id: 'onsale.default.heading.steamDeckCompatibleGames',
    defaultMessage: 'Steam Deck Compatible Games',
  },
});

const DefaultSale = ({ algoliaData }) => {
  const { formatMessage, formatNumber } = useIntl();

  const {
    topSellers,
    latestDeals,
    gamesUnderFive,
    gameBundles,
    newReleases,
    ebooks,
    publishers,
    steamDeck,
    majorSale,
    majorSaleTopSellers,
  } = algoliaData;

  const priceTagUnder5 = formatNumber(floor(pricing['5'].max[currencyCode] / 100, 2), { minimumFractionDigits: 0, style: 'currency', currency: currencyCode });

  const formatPublisherTitle = publisher => `${publisher}${publisher.toLowerCase().includes('games') ? '' : ` ${formatMessage(messages.games)}`}`;

  const topHits = majorSaleTopSellers && majorSaleTopSellers.length ?
    majorSaleTopSellers.slice(0, 7) :
    topSellers.slice(0, 7);

  const otherHits = majorSaleTopSellers && majorSaleTopSellers.length ?
    topSellers.slice(0, 8) :
    topSellers.slice(7, 15);

  return (
    <React.Fragment>
      <section className="container">
        <div className="section-heading-container">
          <h2 className="section-title">
            {majorSale ?
              <>{majorSale.displayName}</> :
              <FormattedMessage id="default-sale.title.default" defaultMessage="Top Sellers" />
            }
          </h2>
          {majorSale ?
            <BrowseButton className={'view-all-btn'} location={`collections/${majorSale.saleSlug}`} /> :
            <BrowseButton className={'view-all-btn'} location={'top-sellers'} />
          }
        </div>
        <ul className="HitCardsRow HitCardsRow--marginReset row">
          {topHits.slice(0, 2).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={6}
              lgSize={4}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            />,
          )}

          {topHits.slice(2, 3).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={4}
              lgSize={4}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            />,
          )}

          {topHits.slice(3, 7).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={4}
              lgSize={3}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            />,
          )}
        </ul>
      </section>

      <FlashDealsSection threeProductTemplate noPadding useTimer />

      <MysteryBundleCardContainer />

      <section className="container">
        <div className="section-heading-container">
          <h2 className="section-title">
            {majorSale &&
              <FormattedMessage id="default-sale.title.default-more" defaultMessage="More Top Sellers" />
            }
            {!majorSale &&
              <FormattedMessage id="default-sale.title.default-down" defaultMessage="Top Sellers" />
            }
          </h2>
          <BrowseButton className={'view-all-btn'} location={'top-sellers'} />
        </div>
        <ul className="HitCardsRow HitCardsRow--marginReset row">
          {otherHits.map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={4}
              lgSize={3}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            />,
          )}
        </ul>
      </section>

      <ProductCarouselSection
        title={formatMessage(messages.gameBundles)}
        viewAllLocation={'bundle'}
        loadedHits={gameBundles}
        viewAllTitle={formatMessage(messages.gameBundles)}
        hitCardNameTag="h3"
        slidesToShow={3}
        lgSize={4}
        itemListTitle="PC Game Bundles"
      />

      <ProductCarouselSection
        title={formatMessage(messages.moreThanJustGames)}
        viewAllLocation={'bundle/books'}
        loadedHits={ebooks}
        viewAllTitle={formatMessage(messages.moreThanJustGames)}
        hitCardNameTag="h3"
        itemListTitle="More Than Just Games"
      />

      <section className="container">
        <div className="games-under-banners section-margin three-banners">
          <GamesUnderBanner
            type="left"
            appearsOn="on_sale"
            to="games-under/5"
            priceUnder={pricing['5'].max}
          />
          <GamesUnderBanner
            type="middle"
            appearsOn="on_sale"
            to="games-under/10"
            priceUnder={pricing['10'].max}
          />
          <GamesUnderBanner
            type="right"
            appearsOn="on_sale"
            to="games-under/20"
            priceUnder={pricing['20'].max}
          />
        </div>
      </section>

      <ProductCarouselSection
        title={formatMessage(messages.newAndUpcoming)}
        viewAllLocation={'new-releases'}
        loadedHits={newReleases}
        viewAllTitle={formatMessage(messages.newAndUpcoming)}
        slidesToShow={3}
        hitCardNameTag="h3"
        itemListTitle="New and Upcoming"
      />

      <FanFavouritesSection onSalePage/>

      <Suspense>
        <ProductFinder onSalePage />
      </Suspense>

      <EmailSignupPlacement needsContainer appearsOn="on_sale" />

      <section className="container">
        <GenreLinks headingTag="h2" />
      </section>

      <PublisherCarousel showDealNumbers />

      <ProductCarouselSection
        title={formatMessage(messages.latestDeals)}
        viewAllLocation={'latest-deals'}
        loadedHits={latestDeals}
        viewAllTitle={formatMessage(messages.latestDeals)}
        hitCardNameTag="h3"
        itemListTitle="Latest Deals"
      />

      <ProductCarouselSection
        title={formatMessage(messages.steamDeckCompatibleGames)}
        viewAllLocation={'steam-deck-games'}
        loadedHits={steamDeck}
        viewAllTitle={formatMessage(messages.steamDeckCompatibleGames)}
        hitCardNameTag="h3"
        itemListTitle="Steam Deck Compatible Games"
      />

      <ProductCarouselSection
        title={formatMessage(messages.dealsUnder, { price: priceTagUnder5 })}
        viewAllLocation="games-under/5"
        loadedHits={gamesUnderFive}
        viewAllTitle={formatMessage(messages.dealsUnder, { price: priceTagUnder5 })}
        hitCardNameTag="h3"
        itemListTitle={`Deals Under ${priceTagUnder5}`}
      />

      <MidPageBannerContainer appearsOn="on_sale" />

      {!isEmpty(publishers) && publishers.map(publisher =>
        <ProductCarouselSection
          sectionCss={`section-margin-carousel ${publisher.name}`}
          title={formatPublisherTitle(startCase(publisher.name))}
          viewAllLocation={`publishers/${encodeSlug(startCase(publisher.name))}`}
          loadedHits={publisher.hits}
          viewAllTitle={formatPublisherTitle(startCase(publisher.name))}
          hitCardNameTag="h3"
          itemListTitle={`${startCase(publisher.name)}${publisher.name.toLowerCase().includes('games') ? '' : ' Games'}`}
        />,
      )}

    </React.Fragment>
  );
};

DefaultSale.propTypes = {
  algoliaData: PropTypes.shape({}),
};

DefaultSale.defaultProps = {
  algoliaData: null,
};

export default DefaultSale;
